/**
 * Shared Animation_Config contract and CSS resolver for the Published Reader.
 *
 * This module is purely additive and CSS-based. It defines the canonical
 * Animation_Config shape, a normalizer that fills/validates an arbitrary
 * persisted value, and `animationStyleFor` which turns a config into a
 * deterministic CSS descriptor consumed by the reader animation runtime
 * (see `templateAnimations.jsx`, task 2.2) and the builder preview.
 *
 * Identity rule: a `type === 'none'` config or an absent config produces NO
 * animation (the resolver returns `null`), so a target without an animation
 * renders exactly as it does today (Requirement 6.9).
 *
 * Validates (conceptually): Requirements 6.8, 6.9.
 */

/** Default Animation_Config. Absent/invalid fields fall back to these. */
export const ANIMATION_DEFAULTS = Object.freeze({
  type: 'none',
  trigger: 'load',
  duration: 600,
  delay: 0,
  easing: 'ease'
});

/** Allowed animation types (the first, `none`, means "no animation"). */
export const ANIMATION_TYPES = Object.freeze([
  'none',
  'fade',
  'slide-in',
  'slide-up',
  'slide-down',
  'slide-left',
  'slide-right',
  'zoom'
]);

/** Allowed animation triggers. */
export const ANIMATION_TRIGGERS = Object.freeze(['load', 'scroll']);

/** Base CSS class applied to every animated region. */
export const ANIMATION_BASE_CLASS = 'avps-anim';

/**
 * Class toggled by the IntersectionObserver to "play" a scroll-triggered
 * animation. The reader runtime adds this class when the region enters view.
 */
export const ANIMATION_PLAY_CLASS = 'avps-anim-play';

/** Upper bounds keep persisted values sane and prevent runaway animations. */
const MAX_DURATION_MS = 60000;
const MAX_DELAY_MS = 60000;
const MAX_EASING_LENGTH = 64;

/**
 * Map an animation type to its keyframes id. The reader runtime registers a
 * single `<style>` block keyed by these ids (e.g. `avps-anim-fade`).
 * @param {string} type
 * @returns {string|null} keyframes id, or null for `none`/unknown types
 */
export const keyframesIdForType = (type) => {
  if (!type || type === 'none' || !ANIMATION_TYPES.includes(type)) return null;
  return `${ANIMATION_BASE_CLASS}-${type}`;
};

const clampNonNegative = (value, fallback, max) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return fallback;
  return Math.min(num, max);
};

const sanitizeEasing = (value) => {
  if (typeof value !== 'string') return ANIMATION_DEFAULTS.easing;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_EASING_LENGTH) return ANIMATION_DEFAULTS.easing;
  // Reject characters that could break out of the CSS value context.
  if (/[<>{};]/.test(trimmed)) return ANIMATION_DEFAULTS.easing;
  return trimmed;
};

/**
 * Merge an arbitrary persisted value over the defaults, validating each field.
 * Unknown `type`/`trigger` fall back to defaults; `duration`/`delay` are clamped
 * to finite, non-negative, bounded numbers; `easing` is sanitized.
 * @param {object|null|undefined} config
 * @returns {{type:string,trigger:string,duration:number,delay:number,easing:string}}
 */
export const normalizeAnimationConfig = (config) => {
  if (!config || typeof config !== 'object') {
    return { ...ANIMATION_DEFAULTS };
  }
  const type = ANIMATION_TYPES.includes(config.type) ? config.type : ANIMATION_DEFAULTS.type;
  const trigger = ANIMATION_TRIGGERS.includes(config.trigger)
    ? config.trigger
    : ANIMATION_DEFAULTS.trigger;
  return {
    type,
    trigger,
    duration: clampNonNegative(config.duration, ANIMATION_DEFAULTS.duration, MAX_DURATION_MS),
    delay: clampNonNegative(config.delay, ANIMATION_DEFAULTS.delay, MAX_DELAY_MS),
    easing: sanitizeEasing(config.easing)
  };
};

/**
 * Resolve an Animation_Config into a CSS descriptor.
 *
 * Returns `null` (identity — no animation) when the config is absent or its
 * normalized type is `none`.
 *
 * For `trigger: 'load'` the returned `style` sets the animation to play
 * immediately. For `trigger: 'scroll'` the returned `style` stages an initial
 * hidden, paused state; the runtime adds `className` (ANIMATION_PLAY_CLASS) when
 * the region scrolls into view to start the animation.
 *
 * @param {object|null|undefined} config
 * @returns {{className:string,style:object,keyframesId:string}|null}
 */
export const animationStyleFor = (config) => {
  if (!config || typeof config !== 'object') return null;
  const normalized = normalizeAnimationConfig(config);
  if (normalized.type === 'none') return null;

  const keyframesId = keyframesIdForType(normalized.type);
  if (!keyframesId) return null;

  const baseAnimation = {
    animationName: keyframesId,
    animationDuration: `${normalized.duration}ms`,
    animationDelay: `${normalized.delay}ms`,
    animationTimingFunction: normalized.easing,
    animationFillMode: 'both'
  };

  if (normalized.trigger === 'scroll') {
    return {
      className: `${ANIMATION_BASE_CLASS} ${ANIMATION_BASE_CLASS}--scroll`,
      keyframesId,
      style: {
        ...baseAnimation,
        // Hidden until the play class is toggled by the IntersectionObserver.
        opacity: 0,
        animationPlayState: 'paused'
      }
    };
  }

  // trigger === 'load': play immediately.
  return {
    className: `${ANIMATION_BASE_CLASS} ${ANIMATION_BASE_CLASS}--load`,
    keyframesId,
    style: {
      ...baseAnimation,
      animationPlayState: 'running'
    }
  };
};

const animationConfig = {
  ANIMATION_DEFAULTS,
  ANIMATION_TYPES,
  ANIMATION_TRIGGERS,
  ANIMATION_BASE_CLASS,
  ANIMATION_PLAY_CLASS,
  keyframesIdForType,
  normalizeAnimationConfig,
  animationStyleFor
};

export default animationConfig;
